import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { User } from '@lms/shared/types';

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...userKeys.all, 'list', params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  loginReadiness: () => [...userKeys.all, 'login-readiness'] as const,
};

export function useParticipantLoginReadiness() {
  return useQuery({
    queryKey: userKeys.loginReadiness(),
    queryFn: () => apiClient.users.getParticipantLoginReadiness(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  excludeProgramId?: string;
  programId?: string;
}) {
  return useQuery({
    queryKey: userKeys.list(params || {}),
    queryFn: () => apiClient.users.list(params),
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => apiClient.users.get(id),
    enabled: !!id,
  });
}

export function useUserDetails(id: string | null) {
  return useQuery({
    queryKey: [...userKeys.details(), id, 'extended'],
    queryFn: () => apiClient.users.getDetails(id!),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<User>) => apiClient.users.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) =>
      apiClient.users.update(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.users.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export function useActivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.users.activate(id),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.users.deactivate(id),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

export function useRecordParticipantPasswordReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.users.recordPasswordResetRequest(id),
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: userKeys.details() });
    },
  });
}

export function useSetParticipantPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      apiClient.users.setParticipantPassword(id, password),
    onSuccess: async (_, { id }) => {
      await queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: userKeys.details() });
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.users.listRoles(),
  });
}
